/**
 * Cart drawer upsell (snippets/cart-drawer-upsell.liquid).
 *
 * Liquid hands us a candidate pool (pinned product + fallback collection,
 * minus anything already in the cart). We rank it with Shopify's Product
 * Recommendations API for the anchor product, render up to `limit` cards,
 * and add to cart the same way product-form.js does so the drawer repaints
 * through cart-drawer.renderContents().
 */

/** @type {Map<string, Promise<Array<object>>>} recommendations per anchor product, kept across drawer re-renders */
const recommendationCache = new Map();

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-cart-upsell-list]');
    this.template = this.querySelector('[data-cart-upsell-card]');
    this.candidates = this.#readCandidates();
    this.inCartProductIds = new Set(JSON.parse(this.dataset.inCart || '[]'));

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-upsell-add]');
      if (button) this.#add(button);
    });

    this.#load();
  }

  #readCandidates() {
    try {
      const raw = this.querySelector('[data-cart-upsell-candidates]')?.textContent || '[]';
      return JSON.parse(raw).filter(Boolean);
    } catch (error) {
      console.error('Cart upsell: bad candidate JSON', error);
      return [];
    }
  }

  async #load() {
    const useRecommendations = this.dataset.useRecommendations === 'true';
    const restrictToPool = this.dataset.restrictToPool === 'true';
    const limit = Number(this.dataset.limit) || 3;

    let recommended = [];
    if (useRecommendations && this.dataset.anchorProductId) {
      recommended = await this.#fetchRecommendations(this.dataset.anchorProductId);
    }

    const byId = new Map(this.candidates.map((c) => [c.productId, c]));
    const ordered = [];
    const push = (item) => {
      if (!item || ordered.some((o) => o.productId === item.productId)) return;
      ordered.push(item);
    };

    // 1. Pinned pick always leads.
    push(this.candidates.find((c) => c.pinned));

    // 2. Recommendations, in Shopify's order. Restricted to the pool when a
    //    collection is configured; otherwise anything in the catalog.
    for (const rec of recommended) {
      if (byId.has(rec.productId)) push(byId.get(rec.productId));
      else if (!restrictToPool) push(rec);
    }

    // 3. Whatever is left of the pool, in collection order.
    for (const candidate of this.candidates) push(candidate);

    this.#render(ordered.slice(0, limit));
  }

  /**
   * @param {string} productId
   * @returns {Promise<Array<object>>} candidates in the API's shape, already filtered
   */
  async #fetchRecommendations(productId) {
    if (!recommendationCache.has(productId)) {
      const url = new URL(this.dataset.recommendationsUrl || '/recommendations/products', window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', 'related');
      // .json suffix returns product JSON instead of a rendered section
      url.pathname = url.pathname.replace(/\/?$/, '.json');

      const request = fetch(url)
        .then((response) => (response.ok ? response.json() : { products: [] }))
        .then(({ products = [] }) => products.map((p) => this.#fromApi(p)).filter(Boolean))
        .catch((error) => {
          console.error('Cart upsell: recommendations failed', error);
          return [];
        });
      recommendationCache.set(productId, request);
    }

    const recs = await recommendationCache.get(productId);
    const anchor = Number(productId);
    return recs.filter((rec) => rec.productId !== anchor && !this.inCartProductIds.has(rec.productId));
  }

  #fromApi(product) {
    const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
    if (!product.available || !variant) return null;
    let image = product.featured_image || '';
    if (image) {
      const imageUrl = new URL(image, window.location.origin);
      imageUrl.searchParams.set('width', '240');
      image = imageUrl.toString();
    }
    return {
      productId: product.id,
      variantId: variant.id,
      title: product.title,
      url: product.url,
      image,
      price: variant.price,
      compareAtPrice: variant.compare_at_price || 0,
      pinned: false,
    };
  }

  #render(items) {
    if (!this.list || !this.template || items.length === 0) {
      this.hidden = true;
      return;
    }

    const format = new Intl.NumberFormat(this.dataset.locale || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    });

    this.list.replaceChildren(
      ...items.map((item) => {
        const card = this.template.content.firstElementChild.cloneNode(true);
        const link = card.querySelector('[data-field="link"]');
        const title = card.querySelector('[data-field="title"]');
        const image = card.querySelector('[data-field="image"]');
        const compare = card.querySelector('[data-field="compare"]');
        const button = card.querySelector('[data-cart-upsell-add]');

        link.href = item.url;
        title.href = item.url;
        title.textContent = item.title;
        if (item.image) image.src = item.image;
        else image.remove();
        card.querySelector('[data-field="price"]').textContent = format.format(item.price / 100);
        if (item.compareAtPrice > item.price) {
          compare.textContent = format.format(item.compareAtPrice / 100);
          compare.hidden = false;
        }
        button.dataset.variantId = item.variantId;
        button.setAttribute('aria-label', `${button.textContent.trim()} ${item.title}`);
        return card;
      })
    );
    this.hidden = false;
  }

  async #add(button) {
    const cart = this.closest('cart-drawer');
    if (!cart || button.classList.contains('loading')) return;

    this.#setLoading(button, true);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', button.dataset.variantId);
    formData.append('quantity', '1');
    formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const state = await response.json();

      if (state.status) {
        console.error('Cart upsell add failed:', state.description || state.message);
        this.#setLoading(button, false);
        return;
      }

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-upsell',
          productVariantId: button.dataset.variantId,
          cartData: state,
        });
      }
      cart.renderContents(state);
    } catch (error) {
      console.error('Cart upsell add failed:', error);
      this.#setLoading(button, false);
    }
  }

  #setLoading(button, isLoading) {
    button.classList.toggle('loading', isLoading);
    button.setAttribute('aria-disabled', isLoading);
    button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
  }
}

customElements.define('cart-upsell', CartUpsell);
